// Meldinger med kunden på en kundeordre (vinnsel 29.10) eller et tekstiltrykk (api="services",
// vinnsel 30.10). Kunden leser og svarer på /kundeordre/ eller /kundetrykk/<token>
// (PublicOrderPortal.jsx); backend: src/lib/kundelenke.js.
// Uleste meldinger fra kunden vises øverst i ordrelisten og på flisen «Ordrevarsler», og
// merkes som lest når ordren åpnes her.
import React, { useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import formatDateTime from '../hjelpere/formatDateTime.js';
import { knappPrimar } from '../ny-kundesamhandling/Skjema';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function OrdreMeldinger({ ordre, kunde, smsLenke, onOppdatert, api = 'orders' }) {
  const trykk = api === 'services';
  const [tekst, setTekst] = useState('');
  const [opptatt, setOpptatt] = useState(false);
  const [status, setStatus] = useState(
    smsLenke === 'sendt' ? 'SMS med kundelenke er sendt til kunden.'
      : smsLenke === 'feil' ? 'Ordren er registrert, men SMS-en med kundelenken kunne ikke sendes. Lenken følger med neste melding du sender.'
        : ''
  );
  const [feil, setFeil] = useState('');
  const listeRef = useRef(null);
  const meldinger = Array.isArray(ordre?.messages) ? ordre.messages : [];
  const avsluttet = (ordre?.Status || ordre?.status) === 'Avsluttet';
  const harTelefon = !!kunde?.phoneNumber;
  const ansatt = Cookies.get('selectedEmployee') || '';

  // Ordren er åpnet: kundens meldinger er lest (varselet forsvinner).
  const harUleste = meldinger.some((m) => m.from === 'customer' && !m.readAt);
  useEffect(() => {
    if (!ordre?._id || !harUleste) return;
    fetch(`${API_BASE_URL}/${api}/${ordre._id}/meldinger/lest`, { method: 'POST' }).catch(() => {});
  }, [ordre?._id, harUleste, api]);

  useEffect(() => {
    if (listeRef.current) listeRef.current.scrollTop = listeRef.current.scrollHeight;
  }, [meldinger.length]);

  const kall = async (sti, body) => {
    const r = await fetch(`${API_BASE_URL}/${api}/${ordre._id}/${sti}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.message || 'Noe gikk galt');
    return data;
  };

  const send = async (e) => {
    e.preventDefault();
    if (!tekst.trim()) return;
    setOpptatt(true);
    setFeil('');
    setStatus('');
    try {
      const data = await kall('meldinger', { tekst, ansatt });
      onOppdatert(data.sak || data.order);
      setTekst('');
      setStatus(
        data.sms === 'sendt' ? 'Meldingen er sendt til kunden på SMS.'
          : data.sms === 'uten-telefon' ? 'Meldingen er lagret. Kunden har ikke telefonnummer og får den ikke på SMS.'
            : `Meldingen er lagret, men SMS-en feilet: ${data.sms}`
      );
    } catch (err) {
      setFeil(err.message);
    } finally {
      setOpptatt(false);
    }
  };

  return (
    <section aria-labelledby="ordre-meldinger" className="space-y-2 rounded-2xl border border-slate-200 bg-white p-3 sm:p-3">
      <h2 id="ordre-meldinger" className="text-base font-bold text-slate-900">Meldinger med kunden</h2>
      <p className="text-sm text-slate-600">
        Meldingen sendes til kunden på SMS, med en lenke der kunden ser {trykk ? 'varen og status' : ordre?.type === 'skiplukk' ? 'status' : 'varen og status'} og kan svare.
      </p>

      {meldinger.length > 0 ? (
        <ul ref={listeRef} className="max-h-80 space-y-2 overflow-y-auto rounded-xl bg-slate-50 p-2.5" aria-label="Meldinger">
          {meldinger.map((m) => {
            const fraKunde = m.from === 'customer';
            return (
              <li key={m._id || m.createdAt} className={`flex ${fraKunde ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[85%] rounded-xl px-3 py-1.5 text-sm ${fraKunde ? 'bg-white border border-slate-200' : 'bg-indigo-50 border border-indigo-100'}`}>
                  <p className="text-xs font-semibold text-slate-700">
                    {fraKunde ? 'Kunden' : m.fromName || 'Butikken'} · {formatDateTime(m.createdAt)}
                    {fraKunde && !m.readAt && <span className="ml-1 rounded bg-amber-100 px-1 text-amber-900">ny</span>}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap break-words text-slate-900">{m.text}</p>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="rounded-xl bg-slate-50 p-2.5 text-sm text-slate-700">Ingen meldinger ennå.</p>
      )}

      {!avsluttet ? (
        <form onSubmit={send} className="space-y-2">
          <label htmlFor="ordre-ny-melding" className="block text-sm font-medium text-slate-800">Ny melding til kunden</label>
          {!harTelefon && <p className="text-sm text-amber-800">Kunden har ikke telefonnummer. Meldingen lagres, men sendes ikke på SMS.</p>}
          <textarea
            id="ordre-ny-melding"
            rows={3}
            maxLength={2000}
            value={tekst}
            onChange={(e) => setTekst(e.target.value)}
            className="w-full rounded-xl border border-slate-300 px-3 py-1.5 text-sm"
          />
          <button type="submit" disabled={opptatt || !tekst.trim()} className={knappPrimar}>
            {opptatt ? 'Sender …' : harTelefon ? 'Send melding på SMS' : 'Lagre melding'}
          </button>
        </form>
      ) : (
        <p className="text-sm text-slate-700">{trykk ? 'Trykket' : 'Ordren'} er avsluttet. Kunden kan ikke sende flere meldinger.</p>
      )}
      {feil && <p role="alert" className="text-sm text-red-700">{feil}</p>}
      <p role="status" className="text-sm text-slate-800">{status}</p>
    </section>
  );
}
